if (!customElements.get('product-info')) {
  customElements.define(
    'product-info',
    class ProductInfo extends HTMLElement {
      quantityInput = undefined;
      quantityForm = undefined;
      onVariantChangeUnsubscriber = undefined;
      cartUpdateUnsubscriber = undefined;
      abortController = undefined;
      pendingRequestUrl = null;
      preProcessHtmlCallbacks = [];
      postProcessHtmlCallbacks = [];

      constructor() {
        super();

        this.quantityInput = this.querySelector('.quantity__input');
      }

      connectedCallback() {
        this.initializeProductSwapUtility();

        this.onVariantChangeUnsubscriber = subscribe(
          PUB_SUB_EVENTS.optionValueSelectionChange,
          this.handleOptionValueChange.bind(this)
        );

        this.initQuantityHandlers();
        this.preloadVariantMedia();
        this.loadPrimaryOptionValues();
        this.dispatchEvent(new CustomEvent('product-info:loaded', { bubbles: true }));
      }

      // ほかの色のメイン画像を、ページが落ち着いてから裏で先読みしておく（クリック時の待ち時間をなくす）
      preloadVariantMedia() {
        if (navigator.connection?.saveData) return;
        const start = () => {
          const image = this.querySelector('media-gallery ul > li[data-media-id] .product__media img');
          const sizes = image?.getAttribute('sizes') || '100vw';
          const seen = new Set();
          this.querySelectorAll('[data-media-srcset]').forEach((source) => {
            const srcset = source.dataset.mediaSrcset;
            if (!srcset || seen.has(srcset)) return;
            seen.add(srcset);
            const preload = new Image();
            preload.decoding = 'async';
            preload.sizes = sizes;
            preload.srcset = srcset;
          });
        };
        const whenIdle = () => ('requestIdleCallback' in window ? requestIdleCallback(start, { timeout: 3000 }) : setTimeout(start, 1500));
        document.readyState === 'complete' ? whenIdle() : window.addEventListener('load', whenIdle, { once: true });
      }

      addPreProcessCallback(callback) {
        this.preProcessHtmlCallbacks.push(callback);
      }

      initQuantityHandlers() {
        if (!this.quantityInput) return;

        this.quantityForm = this.querySelector('.product-form__quantity');
        if (!this.quantityForm) return;

        this.setQuantityBoundries();
        if (!this.dataset.originalSection) {
          this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, this.fetchQuantityRules.bind(this));
        }
      }

      disconnectedCallback() {
        this.onVariantChangeUnsubscriber();
        this.cartUpdateUnsubscriber?.();
      }

      initializeProductSwapUtility() {
        this.preProcessHtmlCallbacks.push((html) =>
          html.querySelectorAll('.scroll-trigger').forEach((element) => element.classList.add('scroll-trigger--cancel'))
        );
        this.postProcessHtmlCallbacks.push((newNode) => {
          window?.Shopify?.PaymentButton?.init();
          window?.ProductModel?.loadShopifyXR();
        });
      }

      handleOptionValueChange({ data: { event, target, selectedOptionValues } }) {
        if (!this.contains(event.target)) return;

        this.resetProductFormState();
        this.startVariantLoading(target);
        this.previewVariantMedia(target);

        const productUrl = target.dataset.productUrl || this.pendingRequestUrl || this.dataset.url;
        this.pendingRequestUrl = productUrl;
        const shouldSwapProduct = this.dataset.url !== productUrl;
        const shouldFetchFullPage = this.dataset.updateUrl === 'true' && shouldSwapProduct;

        this.renderProductInfo({
          requestUrl: this.buildRequestUrlWithParams(productUrl, selectedOptionValues, shouldFetchFullPage),
          targetId: target.id,
          callback: shouldSwapProduct
            ? this.handleSwapProduct(productUrl, shouldFetchFullPage)
            : this.handleUpdateProductInfo(productUrl),
        });
      }

      // サーバーの応答を待たずに、選んだ色の画像を先頭のバリエーション画像に即表示する。
      // 先頭のバリエーション画像はバリエーションを選んで初めて足される（snippets/product-media-gallery.liquid）。
      // まだ無い時（最初の1回）は何も上書きせず、サーバーの応答で先頭に足す（updateMedia）
      previewVariantMedia(target) {
        // 色の目印（alt の「#色名」）がある商品は、画像を入れ替えずに、その色の写真までスクロールする
        const colorMarkerSlot = this.findColorMarkerSlot([target.value]);
        if (colorMarkerSlot) {
          this.scrollToGallerySlot(colorMarkerSlot);
          return;
        }
        // 目印がある商品で、目印の無い色を選んだ時は何も動かさない（先頭への追加やスクロールで、表示位置をずらさない）
        if (this.hasColorMarkers()) return;

        const source = target.tagName === 'SELECT' ? target.selectedOptions[0] : target;
        const { mediaSrc, mediaSrcset, mediaFocalPoint } = source?.dataset || {};
        if (!mediaSrc) return;

        const firstItem = this.querySelector('media-gallery ul > li[data-media-id]');
        if (!firstItem) return;
        const image = firstItem.hasAttribute('data-variant-media') && firstItem.querySelector('.product__media img');

        if (image) this.crossfadeToImage(firstItem, image, mediaSrc, mediaSrcset, mediaFocalPoint);
        firstItem.parentElement.scrollTo({ left: 0 });

        // ページの一番上までスクロール（すでに一番上なら何もしない）
        if (window.scrollY <= 0) return;
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }

      // ギャラリーに alt の「#色名」の目印がある写真が 1 枚でもあるか
      hasColorMarkers() {
        return !!this.querySelector('media-gallery ul > li[data-color-marker]');
      }

      // 翻訳ページ（英語・ドイツ語など）では色名が訳されるが、alt の「#色名」は元の言語のまま。
      // 元の言語の商品データを先に読み込み、「訳した色名 → 元の色名」の対応表を作っておく
      // （元の言語の商品データの URL は snippets/product-variant-picker.liquid）
      loadPrimaryOptionValues() {
        const url = this.querySelector('variant-selects')?.dataset.primaryProductUrl;
        if (!url || !this.hasColorMarkers()) return;

        fetch(url, { headers: { Accept: 'application/json' } })
          .then((response) => (response.ok ? response.json() : null))
          .then((primaryProduct) => {
            if (!primaryProduct?.options) return;
            this.primaryOptionValues = new Map();
            this.querySelectorAll('variant-selects [data-option-value-index]').forEach((element) => {
              const option = primaryProduct.options.find(
                (primaryOption) => String(primaryOption.position) === element.dataset.optionPosition
              );
              const primaryValue = option?.values?.[Number(element.dataset.optionValueIndex)];
              if (!primaryValue) return;
              this.primaryOptionValues.set(String(element.value).trim().toLowerCase(), primaryValue);
            });
          })
          .catch(() => {});
      }

      // ギャラリーで、alt に「#色名」の目印がある写真のうち、渡した選択肢の値（色名）に合うものを探す
      findColorMarkerSlot(values = []) {
        const names = values.filter(Boolean).map((value) => String(value).trim().toLowerCase());
        if (!names.length) return null;
        // 翻訳ページでは、元の言語の色名でも探す
        names.slice().forEach((name) => {
          const primaryValue = this.primaryOptionValues?.get(name);
          if (primaryValue) names.push(String(primaryValue).trim().toLowerCase());
        });
        return Array.from(this.querySelectorAll('media-gallery ul > li[data-color-marker]')).find((slot) =>
          names.includes(slot.dataset.colorMarker.trim().toLowerCase())
        );
      }

      // その写真までスクロールする。PC（縦に並ぶギャラリー）はページを、スマホ（横スライド）はスライダーを動かす
      scrollToGallerySlot(slot) {
        const list = slot.parentElement;
        const smooth = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
        const isSlider = list.scrollWidth > list.clientWidth + 1;

        // スクロール中にヘッダーが出てきて写真に重ならないようにする（常に表示のヘッダーなら、その分ずらす）
        const stickyHeader = document.querySelector('sticky-header');
        stickyHeader?.dispatchEvent(new Event('preventHeaderReveal'));
        const headerOffset = stickyHeader?.dataset.stickyType === 'always' ? stickyHeader.offsetHeight : 0;

        if (isSlider) {
          list.scrollTo({ left: slot.offsetLeft - list.firstElementChild.offsetLeft, behavior: smooth });
          const top = list.getBoundingClientRect().top + window.scrollY - headerOffset;
          if (Math.abs(window.scrollY - top) > 1 && window.scrollY > top) window.scrollTo({ top, behavior: smooth });
          return;
        }

        // ギャラリーの 1 枚目なら、ページの一番上まで戻す（ヘッダーも見せる）
        if (slot === list.firstElementChild) {
          window.scrollTo({ top: 0, behavior: smooth });
          return;
        }
        slot.style.scrollMarginTop = `${headerOffset}px`;
        slot.scrollIntoView({ behavior: smooth, block: 'start' });
      }

      // 新しい画像を読み込み・デコードしてから、上に重ねてクロスフェードで切り替える（読み込み途中を見せない）
      crossfadeToImage(item, image, src, srcset, focalPoint) {
        this.mediaCrossfade?.cancel();
        item.querySelectorAll('.variant-crossfade').forEach((element) => element.remove());

        const overlay = image.cloneNode();
        overlay.removeAttribute('id');
        overlay.classList.add('variant-crossfade');
        overlay.loading = 'eager';
        overlay.style.opacity = '0';
        if (focalPoint) overlay.style.objectPosition = focalPoint;
        overlay.srcset = srcset;
        overlay.src = src;
        image.after(overlay);

        let cancelled = false;
        // 読み込みに 0.2 秒以上かかる時だけローダーを出す（すぐ読めた時にチラつかせない）
        const container = item.querySelector('.product-media-container');
        clearTimeout(this.mediaLoaderTimer);
        this.querySelectorAll('.is-media-loading').forEach((element) => element.classList.remove('is-media-loading'));
        this.mediaLoaderTimer = setTimeout(() => container?.classList.add('is-media-loading'), 200);
        const hideLoader = () => {
          clearTimeout(this.mediaLoaderTimer);
          container?.classList.remove('is-media-loading');
        };

        const run = overlay
          .decode()
          .catch(() => {})
          .then(() => {
            if (cancelled) throw new Error('cancelled');
            hideLoader();
            const duration = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 0 : 450;
            const animation = overlay.animate([{ opacity: 0 }, { opacity: 1 }], {
              duration,
              easing: 'cubic-bezier(0.4, 0, 0.2, 1)',
              fill: 'forwards',
            });
            return animation.finished;
          })
          .then(() => {
            if (cancelled) throw new Error('cancelled');
            if (focalPoint) item.querySelector('.product-media-container')?.style.setProperty('--focal-point', focalPoint);
            image.srcset = srcset;
            image.src = src;
            return image.decode().catch(() => {});
          })
          .then(() => {
            if (!cancelled) overlay.remove();
          })
          .catch(() => {});

        this.mediaCrossfade = {
          promise: run,
          cancel: () => {
            cancelled = true;
            hideLoader();
            overlay.remove();
          },
        };
      }

      // 色などを切り替えている間（サーバーの応答で価格・ボタン・画像が入れ替わるまで）ローダーを出す
      // 色などを切り替えている間の全画面ローダー。
      // 円の線が 12 時から満ちていき（最初は速く、だんだんゆっくり、85% 付近で待つ）、完了したら一周満たしてから消える
      startVariantLoading(target) {
        clearTimeout(this.variantLoadingTimer);
        clearTimeout(this.variantLoadingCompleteTimer);
        this.variantLoadingStartedAt = performance.now();
        this.classList.add('is-variant-loading');

        // 画面全体のローダー（初回だけ作る。古い形が残っていたら作り直す）
        const existingOverlay = document.querySelector('.variant-loading-overlay');
        if (existingOverlay && existingOverlay.dataset.type !== 'fill') existingOverlay.remove();
        if (!document.querySelector('.variant-loading-overlay')) {
          const overlay = document.createElement('div');
          overlay.className = 'variant-loading-overlay';
          overlay.dataset.type = 'fill';
          overlay.setAttribute('role', 'status');
          overlay.setAttribute('aria-live', 'polite');
          overlay.innerHTML =
            '<svg class="variant-loading-overlay__spinner" viewBox="22 22 44 44" aria-hidden="true"><circle class="variant-loading-overlay__track" cx="44" cy="44" r="20.2" fill="none"></circle><circle class="variant-loading-overlay__arc" cx="44" cy="44" r="20.2" fill="none"></circle></svg><span class="visually-hidden">読み込み中</span>';
          document.body.appendChild(overlay);
        }

        // 表示し始める時だけ、線を満たし始める（表示中に続けて切り替えた時はそのまま）。
        // 満ち始める位置は毎回 0〜50% のランダム（いつも空からだと短い待ち時間で動きが見えにくいため）
        const arc = document.querySelector('.variant-loading-overlay__arc');
        const circumference = 127;
        if (arc && !document.documentElement.classList.contains('is-variant-loading')) {
          const startRatio = Math.random() * 0.5;
          arc.style.transition = 'none';
          arc.style.strokeDashoffset = `${circumference * (1 - startRatio)}`;
          void arc.getBoundingClientRect();
          arc.style.transition = 'stroke-dashoffset 2.4s cubic-bezier(0.1, 0.6, 0.3, 1)';
          arc.style.strokeDashoffset = `${circumference * 0.15}`;
        }
        document.documentElement.classList.add('is-variant-loading');
      }

      // 完了したら残りを満たして一周させ、少し見せてから消す（満ちていく様子が見えるよう最低 0.7 秒は出す）
      finishVariantLoading() {
        const elapsed = performance.now() - (this.variantLoadingStartedAt || 0);
        clearTimeout(this.variantLoadingTimer);
        clearTimeout(this.variantLoadingCompleteTimer);
        this.variantLoadingTimer = setTimeout(() => {
          const arc = document.querySelector('.variant-loading-overlay__arc');
          if (arc) {
            arc.style.transition = 'stroke-dashoffset 0.35s cubic-bezier(0.4, 0, 0.2, 1)';
            arc.style.strokeDashoffset = '0';
          }
          this.variantLoadingCompleteTimer = setTimeout(() => {
            document.documentElement.classList.remove('is-variant-loading');
            this.classList.remove('is-variant-loading');
          }, 450);
        }, Math.max(700 - elapsed, 0));
      }

      resetProductFormState() {
        const productForm = this.productForm;
        productForm?.toggleSubmitButton(true);
        productForm?.handleErrorMessage();
      }

      handleSwapProduct(productUrl, updateFullPage) {
        return (html) => {
          this.productModal?.remove();

          const selector = updateFullPage ? "product-info[id^='MainProduct']" : 'product-info';
          const variant = this.getSelectedVariant(html.querySelector(selector));
          this.updateURL(productUrl, variant?.id);

          if (updateFullPage) {
            document.querySelector('head title').innerHTML = html.querySelector('head title').innerHTML;

            HTMLUpdateUtility.viewTransition(
              document.querySelector('main'),
              html.querySelector('main'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          } else {
            HTMLUpdateUtility.viewTransition(
              this,
              html.querySelector('product-info'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          }
        };
      }

      renderProductInfo({ requestUrl, targetId, callback }) {
        this.abortController?.abort();
        this.abortController = new AbortController();

        fetch(requestUrl, { signal: this.abortController.signal })
          .then((response) => response.text())
          .then((responseText) => {
            this.pendingRequestUrl = null;
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            callback(html);
          })
          .then(() => {
            // set focus to last clicked option value
            // （スクロールはさせない。画像の先頭まで上げた位置が、選択肢の位置まで引き戻されるため）
            document.querySelector(`#${targetId}`)?.focus({ preventScroll: true });
          })
          .catch((error) => {
            if (error.name === 'AbortError') {
              console.log('Fetch aborted by user');
            } else {
              console.error(error);
              this.finishVariantLoading();
            }
          });
      }

      getSelectedVariant(productInfoNode) {
        const selectedVariant = productInfoNode.querySelector('variant-selects [data-selected-variant]')?.innerHTML;
        return !!selectedVariant ? JSON.parse(selectedVariant) : null;
      }

      buildRequestUrlWithParams(url, optionValues, shouldFetchFullPage = false) {
        const params = [];

        !shouldFetchFullPage && params.push(`section_id=${this.sectionId}`);

        if (optionValues.length) {
          params.push(`option_values=${optionValues.join(',')}`);
        }

        return `${url}?${params.join('&')}`;
      }

      updateOptionValues(html) {
        const variantSelects = html.querySelector('variant-selects');
        if (variantSelects) {
          HTMLUpdateUtility.viewTransition(this.variantSelectors, variantSelects, this.preProcessHtmlCallbacks);
        }
      }

      handleUpdateProductInfo(productUrl) {
        return (html) => {
          const variant = this.getSelectedVariant(html);

          this.pickupAvailability?.update(variant);
          this.updateOptionValues(html);
          this.updateURL(productUrl, variant?.id);
          this.updateVariantInputs(variant?.id);

          if (!variant) {
            this.setUnavailable();
            this.finishVariantLoading();
            return;
          }

          // 入れ替えで入ってくる画像のフェードインを無効にする（切り替え後のちらつき防止）
          html
            .querySelectorAll('media-gallery .scroll-trigger')
            .forEach((element) => element.classList.add('scroll-trigger--cancel'));

          // クロスフェードが終わってから、新しい先頭画像を事前にデコードしてギャラリーを入れ替える
          const crossfade = this.mediaCrossfade;
          (crossfade?.promise || Promise.resolve())
            .then(() => {
              const incoming = html.querySelector('media-gallery ul > li[data-media-id] .product__media img');
              if (!incoming) return;
              const preload = new Image();
              preload.sizes = incoming.getAttribute('sizes') || '';
              preload.srcset = incoming.getAttribute('srcset') || '';
              preload.src = incoming.getAttribute('src') || '';
              return preload.decode().catch(() => {});
            })
            .then(() => {
              // 途中で別の色が選ばれていたら、古い応答では入れ替えない
              if (crossfade !== this.mediaCrossfade) return;
              const variantSlotSelector = 'media-gallery ul > li[data-variant-media]';
              const hadVariantSlot = !!this.querySelector(variantSlotSelector);
              this.updateMedia(html, variant?.featured_media?.id, variant);
              // 先頭のバリエーション画像が初めて足された時は、ふわっと出す
              const addedSlot = !hadVariantSlot && this.querySelector(variantSlotSelector);
              if (addedSlot && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
                addedSlot.animate([{ opacity: 0 }, { opacity: 1 }], {
                  duration: 450,
                  easing: 'cubic-bezier(0.4, 0, 0.2, 1)',
                });
              }
              this.finishVariantLoading();
            });

          const updateSourceFromDestination = (id, shouldHide = (source) => false) => {
            const source = html.getElementById(`${id}-${this.sectionId}`);
            const destination = this.querySelector(`#${id}-${this.dataset.section}`);
            if (source && destination) {
              destination.innerHTML = source.innerHTML;
              destination.classList.toggle('hidden', shouldHide(source));
            }
          };

          updateSourceFromDestination('price');
          updateSourceFromDestination('Sku', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('Inventory', ({ innerText }) => innerText === '');
          updateSourceFromDestination('Volume');
          updateSourceFromDestination('Price-Per-Item', ({ classList }) => classList.contains('hidden'));

          this.updateQuantityRules(this.sectionId, html);
          this.querySelector(`#Quantity-Rules-${this.dataset.section}`)?.classList.remove('hidden');
          this.querySelector(`#Volume-Note-${this.dataset.section}`)?.classList.remove('hidden');

          this.productForm?.toggleSubmitButton(
            html.getElementById(`ProductSubmitButton-${this.sectionId}`)?.hasAttribute('disabled') ?? true,
            window.variantStrings.soldOut
          );

          publish(PUB_SUB_EVENTS.variantChange, {
            data: {
              sectionId: this.sectionId,
              html,
              variant,
            },
          });
        };
      }

      updateVariantInputs(variantId) {
        this.querySelectorAll(
          `#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`
        ).forEach((productForm) => {
          const input = productForm.querySelector('input[name="id"]');
          input.value = variantId ?? '';
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      }

      updateURL(url, variantId) {
        this.querySelector('share-button')?.updateUrl(
          `${window.shopUrl}${url}${variantId ? `?variant=${variantId}` : ''}`
        );

        if (this.dataset.updateUrl === 'false') return;
        window.history.replaceState({}, '', `${url}${variantId ? `?variant=${variantId}` : ''}`);
      }

      setUnavailable() {
        this.productForm?.toggleSubmitButton(true, window.variantStrings.unavailable);

        const selectors = ['price', 'Inventory', 'Sku', 'Price-Per-Item', 'Volume-Note', 'Volume', 'Quantity-Rules']
          .map((id) => `#${id}-${this.dataset.section}`)
          .join(', ');
        document.querySelectorAll(selectors).forEach(({ classList }) => classList.add('hidden'));
      }

      updateMedia(html, variantFeaturedMediaId, variant) {
        if (!variantFeaturedMediaId) return;

        const mediaGallerySource = this.querySelector('media-gallery ul');
        const mediaGalleryDestination = html.querySelector(`media-gallery ul`);

        const refreshSourceData = () => {
          if (this.hasAttribute('data-zoom-on-hover')) enableZoomOnHover(2);
          const mediaGallerySourceItems = Array.from(mediaGallerySource.querySelectorAll('li[data-media-id]'));
          const sourceSet = new Set(mediaGallerySourceItems.map((item) => item.dataset.mediaId));
          const sourceMap = new Map(
            mediaGallerySourceItems.map((item, index) => [item.dataset.mediaId, { item, index }])
          );
          return [mediaGallerySourceItems, sourceSet, sourceMap];
        };

        if (mediaGallerySource && mediaGalleryDestination) {
          let [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
          const mediaGalleryDestinationItems = Array.from(
            mediaGalleryDestination.querySelectorAll('li[data-media-id]')
          );
          const destinationSet = new Set(mediaGalleryDestinationItems.map(({ dataset }) => dataset.mediaId));
          let shouldRefresh = false;

          // add items from new data not present in DOM
          for (let i = mediaGalleryDestinationItems.length - 1; i >= 0; i--) {
            if (!sourceSet.has(mediaGalleryDestinationItems[i].dataset.mediaId)) {
              mediaGallerySource.prepend(mediaGalleryDestinationItems[i]);
              shouldRefresh = true;
            }
          }

          // remove items from DOM not present in new data
          for (let i = 0; i < mediaGallerySourceItems.length; i++) {
            if (!destinationSet.has(mediaGallerySourceItems[i].dataset.mediaId)) {
              mediaGallerySourceItems[i].remove();
              shouldRefresh = true;
            }
          }

          // refresh
          if (shouldRefresh) [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();

          // if media galleries don't match, sort to match new data order
          mediaGalleryDestinationItems.forEach((destinationItem, destinationIndex) => {
            const sourceData = sourceMap.get(destinationItem.dataset.mediaId);

            if (sourceData && sourceData.index !== destinationIndex) {
              mediaGallerySource.insertBefore(
                sourceData.item,
                mediaGallerySource.querySelector(`li:nth-of-type(${destinationIndex + 1})`)
              );

              // refresh source now that it has been modified
              [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
            }
          });
        }

        // set featured media as active in the media gallery
        // 色の目印（alt の「#色名」）がある商品は、その色の写真を表示中にする（並びは変えない）。
        // 目印の無い色を選んだ時は何もしない（写真を先頭へ動かすと、表示位置がずれるため）。
        // 目印の無い商品は、先頭に追加したバリエーション画像を表示中にする（ギャラリー内の元の画像は動かさない）
        const colorMarkerSlot = this.findColorMarkerSlot(variant?.options);
        if (colorMarkerSlot) {
          this.querySelector(`media-gallery`)?.setActiveMedia?.(colorMarkerSlot.dataset.mediaId, false);
        } else if (!this.hasColorMarkers()) {
          const variantSlot = this.querySelector('media-gallery ul > li[data-variant-media]');
          this.querySelector(`media-gallery`)?.setActiveMedia?.(
            variantSlot?.dataset.mediaId || `${this.dataset.section}-${variantFeaturedMediaId}`,
            true
          );
        }

        // update media modal
        const modalContent = this.productModal?.querySelector(`.product-media-modal__content`);
        const newModalContent = html.querySelector(`product-modal .product-media-modal__content`);
        if (modalContent && newModalContent) modalContent.innerHTML = newModalContent.innerHTML;
      }

      setQuantityBoundries() {
        const data = {
          cartQuantity: this.quantityInput.dataset.cartQuantity ? parseInt(this.quantityInput.dataset.cartQuantity) : 0,
          min: this.quantityInput.dataset.min ? parseInt(this.quantityInput.dataset.min) : 1,
          max: this.quantityInput.dataset.max ? parseInt(this.quantityInput.dataset.max) : null,
          step: this.quantityInput.step ? parseInt(this.quantityInput.step) : 1,
        };

        let min = data.min;
        const max = data.max === null ? data.max : data.max - data.cartQuantity;
        if (max !== null) min = Math.min(min, max);
        if (data.cartQuantity >= data.min) min = Math.min(min, data.step);

        this.quantityInput.min = min;

        if (max) {
          this.quantityInput.max = max;
        } else {
          this.quantityInput.removeAttribute('max');
        }
        this.quantityInput.value = min;

        publish(PUB_SUB_EVENTS.quantityUpdate, undefined);
      }

      fetchQuantityRules() {
        const currentVariantId = this.productForm?.variantIdInput?.value;
        if (!currentVariantId) return;

        this.querySelector('.quantity__rules-cart .loading__spinner').classList.remove('hidden');
        return fetch(`${this.dataset.url}?variant=${currentVariantId}&section_id=${this.dataset.section}`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            this.updateQuantityRules(this.dataset.section, html);
          })
          .catch((e) => console.error(e))
          .finally(() => this.querySelector('.quantity__rules-cart .loading__spinner').classList.add('hidden'));
      }

      updateQuantityRules(sectionId, html) {
        if (!this.quantityInput) return;
        this.setQuantityBoundries();

        const quantityFormUpdated = html.getElementById(`Quantity-Form-${sectionId}`);
        const selectors = ['.quantity__input', '.quantity__rules', '.quantity__label'];
        for (let selector of selectors) {
          const current = this.quantityForm.querySelector(selector);
          const updated = quantityFormUpdated.querySelector(selector);
          if (!current || !updated) continue;
          if (selector === '.quantity__input') {
            const attributes = ['data-cart-quantity', 'data-min', 'data-max', 'step'];
            for (let attribute of attributes) {
              const valueUpdated = updated.getAttribute(attribute);
              if (valueUpdated !== null) {
                current.setAttribute(attribute, valueUpdated);
              } else {
                current.removeAttribute(attribute);
              }
            }
          } else {
            current.innerHTML = updated.innerHTML;
            if (selector === '.quantity__label') {
              const updatedAriaLabelledBy = updated.getAttribute('aria-labelledby');
              if (updatedAriaLabelledBy) {
                current.setAttribute('aria-labelledby', updatedAriaLabelledBy);
                // Update the referenced visually hidden element
                const labelId = updatedAriaLabelledBy;
                const currentHiddenLabel = document.getElementById(labelId);
                const updatedHiddenLabel = html.getElementById(labelId);
                if (currentHiddenLabel && updatedHiddenLabel) {
                  currentHiddenLabel.textContent = updatedHiddenLabel.textContent;
                }
              }
            }
          }
        }
      }

      get productForm() {
        return this.querySelector(`product-form`);
      }

      get productModal() {
        return document.querySelector(`#ProductModal-${this.dataset.section}`);
      }

      get pickupAvailability() {
        return this.querySelector(`pickup-availability`);
      }

      get variantSelectors() {
        return this.querySelector('variant-selects');
      }

      get relatedProducts() {
        const relatedProductsSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'related-products'
        );
        return document.querySelector(`product-recommendations[data-section-id^="${relatedProductsSectionId}"]`);
      }

      get quickOrderList() {
        const quickOrderListSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'quick_order_list'
        );
        return document.querySelector(`quick-order-list[data-id^="${quickOrderListSectionId}"]`);
      }

      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }
    }
  );
}
